import { FC, useCallback, useEffect, useMemo, useState } from 'react'

import { useMutation } from '@tanstack/react-query'
import { routes } from 'commons/constants/routes'
import { CompanyDetailsWebPage } from 'commons/types/DTO/bank'
import { CompanyDetailsHighRiskReview } from 'commons/types/DTO/bank/companyDetails/companyDetailsWebPage'
import { TableDataQa } from 'commons/types/enums'
import { Request } from 'commons/utils/request'
import { DateTime, Layer, Link, PageSectionPrimary, Table, TableActions, Toast, useTableState } from 'ui/components'
import { Button } from 'ui/components/Buttons'
import { useConfirmation } from 'ui/components/Dialog/ConfirmationDialogV2'

import { handlerServerError } from '@orbit/errors'

import { AddDialog } from './components'

export const HighRiskReview: FC<{ data: CompanyDetailsWebPage }> = ({ data }) => {
  const { id, companyName, showHighRiskReview, showHighRiskReviewTotalCount } = data
  const [hrr, setHrr] = useState(showHighRiskReview)
  const [totalCount, setTotalCount] = useState(showHighRiskReviewTotalCount)

  const defaultFilter = useMemo(
    () => ({
      relationshipId: id,
      page: undefined,
      size: undefined
    }),
    [id]
  )

  const [tableFilter, setTableFilter] = useState<{ page?: number; relationshipId: number; size?: number }>(
    defaultFilter
  )
  const tableState = useTableState({ filter: {} })

  const { mutate: filterHrr, isPending } = useMutation<
    { highRiskReview: CompanyDetailsHighRiskReview[]; highRiskReviewTotalCount: number },
    Error,
    { page?: number; relationshipId: number; size?: number }
  >({
    mutationFn: (opts) => Request.post(Request.urls.bank.companyHrr, opts),
    onSuccess: (result) => {
      setHrr(result.highRiskReview)
      setTotalCount(result.highRiskReviewTotalCount)
    },
    onError: (error) => handlerServerError(error)
  })

  const changePagination = useCallback(
    async (page: number, size: number) => {
      tableState.onPageChange(page, size)

      const newFilter = {
        ...tableFilter,
        quarterlyAnalyticsOffset: (page - 1) * size,
        quarterlyAnalyticsLimit: size
      }

      setTableFilter(newFilter)
      filterHrr(newFilter)
    },
    [filterHrr, tableFilter, tableState]
  )

  useEffect(() => {
    setHrr(showHighRiskReview)
  }, [showHighRiskReview])

  const [showDialog, setShowDialog] = useState(false)
  const confirmation = useConfirmation()

  const { mutate } = useMutation<void, Error, { id: number }>({
    mutationFn: (opts) => Request.delete(Request.urls.bank.hrr, opts),
    onSuccess: () => {
      Toast.successSmall(`Review was deleted`)
      filterHrr(tableFilter)
    }
  })

  const handleDelete = async (id: number, period: string) => {
    await confirmation.showDeleteConfirmation({
      message: `Are you sure you want to delete ${period}?`,
      submitButtonText: 'Delete',
      confirmCallback: async () => mutate({ id })
    })
  }

  return (
    <PageSectionPrimary
      title="High-Risk Review"
      anchor="hrr"
      face="outlined"
      actions={
        <Button face="tertiary" data-qa="New High-Risk Review" onClick={() => setShowDialog(true)}>
          Add New
        </Button>
      }
    >
      {showDialog && <AddDialog onClose={() => setShowDialog(false)} companyId={id} companyName={companyName} />}
      <Layer rounded shadowed stretch>
        <Table
          dataQa={TableDataQa.BP_COMPANY_HRR}
          columns={[
            {
              title: 'Period',
              align: 'left',
              width: '250px',
              sortable: false,
              render: ({ period, id }) => (
                <Link to={routes.bank.hrr.details} params={{ id: id.toString() }}>
                  {period}
                </Link>
              )
            },
            {
              title: 'Status',
              align: 'center',
              sortable: false,
              width: '100px',
              key: 'status'
            },
            {
              title: 'Last Modified Date',
              sortable: false,
              align: 'left',
              render: ({ lastModifiedDate }) => <DateTime date={lastModifiedDate} withTime />
            },
            {
              title: 'Actions',
              sortable: false,
              width: '100px',
              render: ({ id, period }) => (
                <TableActions
                  items={[
                    {
                      type: 'delete',
                      onClick: () => handleDelete(id, period)
                    }
                  ]}
                  dataQaAttribute={`hrr-menu-dots-button-${id}`}
                />
              )
            }
          ]}
          dataSource={hrr.map((el) => ({
            item: el
          }))}
          loading={isPending}
          pagination={{
            page: tableState.page,
            size: tableState.size,
            onChange: changePagination,
            total: totalCount
          }}
        />
      </Layer>
    </PageSectionPrimary>
  )
}
